import type { AdminUserDetailDto } from '@carcheck/shared';
import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { RoleBadge, StatusBadge } from './UserBadges';
import { tokens } from '../../../design';
import { initials, shortId } from '../_lib/displayName';

/**
 * Detail-drawer header (AUT-124): avatar + name + role/status badges +
 * short id + close button. Extracted from the drawer to keep the body
 * under the line cap.
 */
export const UserDetailHeader = ({
  user,
  onClose,
}: {
  user: AdminUserDetailDto;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.75,
        p: '18px 24px',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Avatar sx={{ width: 52, height: 52, fontSize: 18, fontWeight: 700 }}>
        {initials(user.displayName)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px' }} noWrap>
          {user.displayName ?? t('users.detail.noValue')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
          <RoleBadge role={user.role} />
          <StatusBadge status={user.status} />
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: tokens.text.tertiary }}>
            {shortId(user.id)}
          </Typography>
        </Box>
      </Box>
      <IconButton onClick={onClose} sx={{ ml: 'auto' }} aria-label={t('users.detail.close')}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
